import type { FC } from 'react';
import React, { useContext, useEffect, useState } from 'react';

import { SpoilerContext } from './../ricos-context';
import type { Node, SpoilerData } from './../ricos-schema/legacy/wix/rich_content/v1';
import { Decoration_Type } from './../ricos-schema/legacy/wix/rich_content/v1';
import type { NodeViewDecorator, SpoilerDecoration } from './../ricos-types';

type TextSpoilerProps = {
  children: JSX.Element;
  spoilerData?: SpoilerData;
};

const TextSpoiler: FC<TextSpoilerProps> = ({ children, spoilerData }) => {
  const { spoilerManager } = useContext(SpoilerContext);
  const { id } = spoilerData || {};
  const [isSpoilerActive, setIsSpoilerActive] = useState(id ? spoilerManager.isActive(id) : true);

  useEffect(() => {
    if (id) {
      spoilerManager?.registerSpoiler(id, () => setIsSpoilerActive(false));
    }
  }, []);

  const toggleSpoiler = () => {
    const { id } = spoilerData || {};
    id ? spoilerManager?.toggleSpoiler(id) : setIsSpoilerActive(!isSpoilerActive);
  };

  return isSpoilerActive ? (
    <span
      role="button"
      tabIndex={0}
      onKeyDown={toggleSpoiler}
      onClick={toggleSpoiler}
      style={{ filter: 'blur(0.25em)', cursor: 'pointer' }}
    >
      {children}
    </span>
  ) : (
    <>{children}</>
  );
};

const decorator = (element: JSX.Element, spoilerDecoration: SpoilerDecoration) => {
  const { spoilerData } = spoilerDecoration;
  return (
    <TextSpoiler key={`spoiler-${element.key}`} spoilerData={spoilerData}>
      {element}
    </TextSpoiler>
  );
};

const SpoilerDecorator: NodeViewDecorator<JSX.Element> = {
  rule: (node: Node) =>
    !!node.textData?.decorations?.some(({ type }) => type === Decoration_Type.SPOILER),
  decorate: ({ element, node }) => {
    const spoilerDecoration = node.textData?.decorations?.find(
      ({ type }) => type === Decoration_Type.SPOILER
    ) as SpoilerDecoration;
    return decorator(element as JSX.Element, spoilerDecoration);
  },
};

export default SpoilerDecorator;
